import { useEffect, useState, type FormEvent } from "react";
import { ApiError } from "@/lib/api";
import { Button, Dialog, Field, Input, Select } from "@/components/ui/primitives";
import { useCustomers } from "@/features/customers/hooks";
import { useContracts, useCreateAsset, useUpdateAsset } from "./hooks";
import { ASSET_STATUS_LABELS, ASSET_TYPE_LABELS } from "./labels";
import type { AssetDetail, AssetStatus, AssetType } from "./api";

interface Props {
  open: boolean;
  onClose: () => void;
  editing: AssetDetail | null;
}

const EMPTY = {
  customer_id: "",
  name: "",
  asset_tag: "",
  asset_type: "hardware" as AssetType,
  category: "",
  manufacturer: "",
  model: "",
  serial_number: "",
  status: "active" as AssetStatus,
  purchase_date: "",
  purchase_cost: "",
  warranty_expiry_date: "",
  assigned_to: "",
  amc_contract_id: "",
  notes: "",
};

export function AssetFormDialog({ open, onClose, editing }: Props) {
  const create = useCreateAsset();
  const update = useUpdateAsset();
  const [form, setForm] = useState({ ...EMPTY });
  const [error, setError] = useState<string | null>(null);

  // Customers to choose from (create only); contracts scoped to the chosen one.
  const { data: customers } = useCustomers({ page_size: 100 }, { enabled: open });
  const { data: contracts } = useContracts(
    { customer_id: form.customer_id, page_size: 100 },
    { enabled: open && form.customer_id !== "" },
  );

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        customer_id: editing.customer_id,
        name: editing.name,
        asset_tag: editing.asset_tag,
        asset_type: editing.asset_type,
        category: editing.category ?? "",
        manufacturer: editing.manufacturer ?? "",
        model: editing.model ?? "",
        serial_number: editing.serial_number ?? "",
        status: editing.status,
        purchase_date: editing.purchase_date ?? "",
        purchase_cost: editing.purchase_cost ?? "",
        warranty_expiry_date: editing.warranty_expiry_date ?? "",
        assigned_to: editing.assigned_to ?? "",
        amc_contract_id: editing.amc_contract_id ?? "",
        notes: editing.notes ?? "",
      });
    } else {
      setForm({ ...EMPTY });
    }
    setError(null);
  }, [open, editing]);

  const set = (k: keyof typeof EMPTY) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const body = {
      name: form.name.trim(),
      asset_tag: form.asset_tag.trim() || null,
      asset_type: form.asset_type,
      category: form.category.trim() || null,
      manufacturer: form.manufacturer.trim() || null,
      model: form.model.trim() || null,
      serial_number: form.serial_number.trim() || null,
      status: form.status,
      purchase_date: form.purchase_date || null,
      purchase_cost: form.purchase_cost.trim() || null,
      warranty_expiry_date: form.warranty_expiry_date || null,
      assigned_to: form.assigned_to.trim() || null,
      amc_contract_id: form.amc_contract_id || null,
      notes: form.notes.trim() || null,
    };
    try {
      if (editing) {
        await update.mutateAsync({ id: editing.id, body });
      } else {
        await create.mutateAsync({ customer_id: form.customer_id, ...body });
      }
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the asset.");
    }
  }

  const canSubmit = form.name.trim() !== "" && (editing !== null || form.customer_id !== "");

  return (
    <Dialog open={open} onClose={onClose} title={editing ? "Edit asset" : "Add asset"}>
      <form onSubmit={submit} className="space-y-3">
        {error && (
          <div role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        {!editing && (
          <Field label="Customer" htmlFor="a_customer">
            <Select
              id="a_customer"
              value={form.customer_id}
              onChange={(e) => {
                set("customer_id")(e.target.value);
                set("amc_contract_id")(""); // contracts are customer-scoped
              }}
            >
              <option value="">Select a customer…</option>
              {(customers?.items ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.company_name}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <Field label="Name" htmlFor="a_name">
          <Input
            id="a_name"
            value={form.name}
            onChange={(e) => set("name")(e.target.value)}
            placeholder="e.g. Dell OptiPlex 7090"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Asset tag (optional)" htmlFor="a_tag">
            <Input
              id="a_tag"
              value={form.asset_tag}
              onChange={(e) => set("asset_tag")(e.target.value)}
              placeholder="Auto-assigned if blank"
            />
          </Field>
          <Field label="Type" htmlFor="a_type">
            <Select
              id="a_type"
              value={form.asset_type}
              onChange={(e) => set("asset_type")(e.target.value)}
            >
              {Object.entries(ASSET_TYPE_LABELS).map(([v, label]) => (
                <option key={v} value={v}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Category" htmlFor="a_cat">
            <Input
              id="a_cat"
              value={form.category}
              onChange={(e) => set("category")(e.target.value)}
              placeholder="e.g. Laptop, Router"
            />
          </Field>
          <Field label="Status" htmlFor="a_status">
            <Select id="a_status" value={form.status} onChange={(e) => set("status")(e.target.value)}>
              {Object.entries(ASSET_STATUS_LABELS).map(([v, label]) => (
                <option key={v} value={v}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Manufacturer" htmlFor="a_mfr">
            <Input id="a_mfr" value={form.manufacturer} onChange={(e) => set("manufacturer")(e.target.value)} />
          </Field>
          <Field label="Model" htmlFor="a_model">
            <Input id="a_model" value={form.model} onChange={(e) => set("model")(e.target.value)} />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Serial number" htmlFor="a_serial">
            <Input id="a_serial" value={form.serial_number} onChange={(e) => set("serial_number")(e.target.value)} />
          </Field>
          <Field label="Assigned to" htmlFor="a_assigned">
            <Input
              id="a_assigned"
              value={form.assigned_to}
              onChange={(e) => set("assigned_to")(e.target.value)}
              placeholder="Person or dept"
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Purchase date" htmlFor="a_pdate">
            <Input id="a_pdate" type="date" value={form.purchase_date} onChange={(e) => set("purchase_date")(e.target.value)} />
          </Field>
          <Field label="Warranty expiry" htmlFor="a_warr">
            <Input
              id="a_warr"
              type="date"
              value={form.warranty_expiry_date}
              onChange={(e) => set("warranty_expiry_date")(e.target.value)}
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Purchase cost (₹)" htmlFor="a_cost">
            <Input
              id="a_cost"
              type="number"
              min="0"
              step="0.01"
              value={form.purchase_cost}
              onChange={(e) => set("purchase_cost")(e.target.value)}
            />
          </Field>
          <Field label="AMC / warranty contract" htmlFor="a_contract">
            <Select
              id="a_contract"
              value={form.amc_contract_id}
              disabled={form.customer_id === ""}
              onChange={(e) => set("amc_contract_id")(e.target.value)}
            >
              <option value="">None</option>
              {(contracts?.items ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.contract_number}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={!canSubmit || create.isPending || update.isPending}>
            {editing ? "Save changes" : "Create asset"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
